import React, { useState, useEffect } from 'react';
import workOrdersService from '../../services/workOrdersService';
import './CreateWorkOrderModal.css';

const PRINT_LOCATIONS = [
  { key: 'front', label: 'Front' },
  { key: 'back', label: 'Back' },
  { key: 'sleeve_r', label: 'Sleeve R' },
  { key: 'sleeve_l', label: 'Sleeve L' },
  { key: 'heart', label: 'Heart' },
  { key: 'on_pocket', label: 'On Pocket' }
];

const EMPTY_INK_ROW = { color: '', instructions: '', print_type_1: '', print_type_2: '', qty: '', amount: '', total: '' };
const EMPTY_GARMENT_ROW = { brand: '', sku: '', color: '', xs: '', s: '', m: '', l: '', xl: '', xxl: '', xxxl: '', xxxxl: '', xxxxxl: '' };

const CreateWorkOrderModal = ({ isOpen, onClose, onSuccess, editingJob }) => {
  const [formData, setFormData] = useState({
    customer: '',
    contact_phone: '',
    contact_email: '',
    invoice_number: '',
    order_date: new Date().toISOString().split('T')[0],
    due_date: '',
    salesperson: '',
    print_locations: { front: false, back: false, sleeve_r: false, sleeve_l: false, heart: false, on_pocket: false },
    print_by: '',
    print_date: '',
    special_instructions: '',
    ink_details: [{ ...EMPTY_INK_ROW }],
    garment_details: [{ ...EMPTY_GARMENT_ROW }],
    total_with_tax: '',
    deposit: '',
    total_due: '',
    priority: 'normal',
    notes: '',
    internal_notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // Populate form when editing
  useEffect(() => {
    if (editingJob) {
      setFormData({
        customer: editingJob.customer || '',
        contact_phone: editingJob.contact_phone || '',
        contact_email: editingJob.contact_email || '',
        invoice_number: editingJob.invoice_number || '',
        order_date: editingJob.order_date ? editingJob.order_date.split('T')[0] : new Date().toISOString().split('T')[0],
        due_date: editingJob.due_date ? editingJob.due_date.split('T')[0] : '',
        salesperson: editingJob.salesperson || '',
        print_locations: editingJob.print_locations || { front: false, back: false, sleeve_r: false, sleeve_l: false, heart: false, on_pocket: false },
        print_by: editingJob.print_by || '',
        print_date: editingJob.print_date ? editingJob.print_date.split('T')[0] : '',
        special_instructions: editingJob.special_instructions || '',
        ink_details: editingJob.ink_details?.length ? editingJob.ink_details : [{ ...EMPTY_INK_ROW }],
        garment_details: editingJob.garment_details?.length ? editingJob.garment_details : [{ ...EMPTY_GARMENT_ROW }],
        total_with_tax: editingJob.total_with_tax || '',
        deposit: editingJob.deposit || '',
        total_due: editingJob.total_due || '',
        priority: editingJob.priority || 'normal',
        notes: editingJob.notes || '',
        internal_notes: editingJob.internal_notes || ''
      });
    } else {
      // Reset form for new work order
      setFormData({
        customer: '',
        contact_phone: '',
        contact_email: '',
        invoice_number: '',
        order_date: new Date().toISOString().split('T')[0],
        due_date: '',
        salesperson: '',
        print_locations: { front: false, back: false, sleeve_r: false, sleeve_l: false, heart: false, on_pocket: false },
        print_by: '',
        print_date: '',
        special_instructions: '',
        ink_details: [{ ...EMPTY_INK_ROW }],
        garment_details: [{ ...EMPTY_GARMENT_ROW }],
        total_with_tax: '',
        deposit: '',
        total_due: '',
        priority: 'normal',
        notes: '',
        internal_notes: ''
      });
    }
    setError(null);
    setErrors({});
    setSuccess(false);
  }, [editingJob, isOpen]);

  // Format phone number
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.customer.trim()) {
      newErrors.customer = 'Customer name is required';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;

    if (name === 'contact_phone') {
      processedValue = formatPhoneNumber(value);
    }

    if (type === 'checkbox') {
      processedValue = checked;
    }

    setFormData({
      ...formData,
      [name]: processedValue
    });

    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Handle print location change
  const handlePrintLocationChange = (key) => {
    setFormData({
      ...formData,
      print_locations: {
        ...formData.print_locations,
        [key]: !formData.print_locations[key]
      }
    });
  };

  // Handle ink details change
  const handleInkChange = (index, field, value) => {
    const newInkDetails = [...formData.ink_details];
    newInkDetails[index] = { ...newInkDetails[index], [field]: value };

    // Auto-calculate total
    if (field === 'qty' || field === 'amount') {
      const qty = parseFloat(newInkDetails[index].qty) || 0;
      const amount = parseFloat(newInkDetails[index].amount) || 0;
      newInkDetails[index].total = (qty * amount).toFixed(2);
    }

    setFormData({ ...formData, ink_details: newInkDetails });
  };

  const addInkRow = () => {
    if (formData.ink_details.length < 8) {
      setFormData({
        ...formData,
        ink_details: [...formData.ink_details, { ...EMPTY_INK_ROW }]
      });
    }
  };

  const removeInkRow = (index) => {
    if (formData.ink_details.length > 1) {
      const newInkDetails = formData.ink_details.filter((_, i) => i !== index);
      setFormData({ ...formData, ink_details: newInkDetails });
    }
  };

  // Handle garment details change
  const handleGarmentChange = (index, field, value) => {
    const newGarmentDetails = [...formData.garment_details];
    newGarmentDetails[index] = { ...newGarmentDetails[index], [field]: value };
    setFormData({ ...formData, garment_details: newGarmentDetails });
  };

  const addGarmentRow = () => {
    setFormData({
      ...formData,
      garment_details: [...formData.garment_details, { ...EMPTY_GARMENT_ROW }]
    });
  };

  const removeGarmentRow = (index) => {
    if (formData.garment_details.length > 1) {
      const newGarmentDetails = formData.garment_details.filter((_, i) => i !== index);
      setFormData({ ...formData, garment_details: newGarmentDetails });
    }
  };

  // Calculate total due when total_with_tax or deposit changes
  useEffect(() => {
    const total = parseFloat(formData.total_with_tax) || 0;
    const deposit = parseFloat(formData.deposit) || 0;
    const totalDue = total - deposit;
    if (totalDue >= 0) {
      setFormData(prev => ({ ...prev, total_due: totalDue.toFixed(2) }));
    }
  }, [formData.total_with_tax, formData.deposit]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Clean up empty rows
      const cleanedInkDetails = formData.ink_details.filter(ink => ink.color || ink.instructions);
      const cleanedGarmentDetails = formData.garment_details.filter(garment => garment.brand || garment.sku || garment.color);

      const submitData = {
        ...formData,
        ink_details: cleanedInkDetails.length > 0 ? cleanedInkDetails : [],
        garment_details: cleanedGarmentDetails.length > 0 ? cleanedGarmentDetails : [],
        total_with_tax: formData.total_with_tax ? parseFloat(formData.total_with_tax) : null,
        deposit: formData.deposit ? parseFloat(formData.deposit) : 0,
        total_due: formData.total_due ? parseFloat(formData.total_due) : null
      };

      if (editingJob) {
        await workOrdersService.updateWorkOrder(editingJob.id, submitData);
      } else {
        await workOrdersService.createWorkOrder(submitData);
      }

      setSuccess(true);

      setTimeout(() => {
        onSuccess?.();
        setSuccess(false);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${editingJob ? 'update' : 'create'} work order`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="wo-modal-overlay active" onClick={onClose}>
      <div className="wo-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="wo-modal-header">
          <h2>{editingJob ? 'Edit Work Order' : 'Create Work Order'}</h2>
          <button className="wo-modal-close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="wo-success-message">
            Work order {editingJob ? 'updated' : 'created'} successfully!
          </div>
        )}

        {/* Error Message */}
        {error && <div className="wo-error-message">{error}</div>}

        {/* Modal Body */}
        <div className="wo-modal-body">
          <form onSubmit={handleSubmit} className="wo-form">
            {/* Header Section */}
            <div className="wo-form-section">
              <h4>Customer Information</h4>
              <div className="wo-form-row">
                <div className="wo-form-group wo-form-group-lg">
                  <label htmlFor="customer">
                    Customer <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="customer"
                    name="customer"
                    value={formData.customer}
                    onChange={handleChange}
                    placeholder="Customer name"
                    className={errors.customer ? 'input-error' : ''}
                  />
                  {errors.customer && <span className="error-text">{errors.customer}</span>}
                </div>
                <div className="wo-form-group">
                  <label htmlFor="contact_phone">Phone</label>
                  <input
                    type="tel"
                    id="contact_phone"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="123-456-7890"
                  />
                </div>
                <div className="wo-form-group">
                  <label htmlFor="contact_email">Email</label>
                  <input
                    type="email"
                    id="contact_email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="wo-form-row">
                <div className="wo-form-group">
                  <label htmlFor="invoice_number">Invoice #</label>
                  <input
                    type="text"
                    id="invoice_number"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleChange}
                    placeholder="Invoice number"
                  />
                </div>
                <div className="wo-form-group">
                  <label htmlFor="order_date">Order Date</label>
                  <input
                    type="date"
                    id="order_date"
                    name="order_date"
                    value={formData.order_date}
                    onChange={handleChange}
                  />
                </div>
                <div className="wo-form-group">
                  <label htmlFor="due_date">Due Date</label>
                  <input
                    type="date"
                    id="due_date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleChange}
                  />
                </div>
                <div className="wo-form-group">
                  <label htmlFor="salesperson">Salesperson</label>
                  <input
                    type="text"
                    id="salesperson"
                    name="salesperson"
                    value={formData.salesperson}
                    onChange={handleChange}
                    placeholder="Salesperson name"
                  />
                </div>
              </div>
            </div>

            {/* Print Locations */}
            <div className="wo-form-section">
              <h4>Print Locations</h4>
              <div className="wo-checkbox-row">
                {PRINT_LOCATIONS.map(loc => (
                  <label key={loc.key} className="wo-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.print_locations[loc.key]}
                      onChange={() => handlePrintLocationChange(loc.key)}
                    />
                    <span className="wo-checkbox-custom"></span>
                    {loc.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Production Section */}
            <div className="wo-form-section">
              <h4>Production</h4>
              <div className="wo-form-row">
                <div className="wo-form-group">
                  <label htmlFor="print_by">Print By</label>
                  <input
                    type="text"
                    id="print_by"
                    name="print_by"
                    value={formData.print_by}
                    onChange={handleChange}
                    placeholder="Printer name"
                  />
                </div>
                <div className="wo-form-group">
                  <label htmlFor="print_date">Print Date</label>
                  <input
                    type="date"
                    id="print_date"
                    name="print_date"
                    value={formData.print_date}
                    onChange={handleChange}
                  />
                </div>
                <div className="wo-form-group">
                  <label htmlFor="priority">Priority</label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="rush">Rush</option>
                  </select>
                </div>
              </div>
              <div className="wo-form-group">
                <label htmlFor="special_instructions">Special Instructions</label>
                <textarea
                  id="special_instructions"
                  name="special_instructions"
                  value={formData.special_instructions}
                  onChange={handleChange}
                  placeholder="Any special instructions for production..."
                  rows="2"
                />
              </div>
            </div>

            {/* Ink Details */}
            <div className="wo-form-section">
              <div className="wo-section-header">
                <h4>Ink Details</h4>
                {formData.ink_details.length < 8 && (
                  <button type="button" className="wo-btn-add-row" onClick={addInkRow}>
                    + Add Row
                  </button>
                )}
              </div>
              <div className="wo-table-wrapper">
                <table className="wo-edit-table">
                  <thead>
                    <tr>
                      <th>Color</th>
                      <th>Instructions</th>
                      <th>Print Type 1</th>
                      <th>Print Type 2</th>
                      <th>Qty</th>
                      <th>Amount</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.ink_details.map((ink, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={ink.color}
                            onChange={(e) => handleInkChange(index, 'color', e.target.value)}
                            placeholder="Color"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ink.instructions}
                            onChange={(e) => handleInkChange(index, 'instructions', e.target.value)}
                            placeholder="Instructions"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ink.print_type_1}
                            onChange={(e) => handleInkChange(index, 'print_type_1', e.target.value)}
                            placeholder="Type"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ink.print_type_2}
                            onChange={(e) => handleInkChange(index, 'print_type_2', e.target.value)}
                            placeholder="Type"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={ink.qty}
                            onChange={(e) => handleInkChange(index, 'qty', e.target.value)}
                            placeholder="0"
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={ink.amount}
                            onChange={(e) => handleInkChange(index, 'amount', e.target.value)}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ink.total}
                            readOnly
                            placeholder="0.00"
                            className="wo-readonly-input"
                          />
                        </td>
                        <td>
                          {formData.ink_details.length > 1 && (
                            <button
                              type="button"
                              className="wo-btn-remove-row"
                              onClick={() => removeInkRow(index)}
                            >
                              &times;
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Garment Details */}
            <div className="wo-form-section">
              <div className="wo-section-header">
                <h4>Garment Details</h4>
                <button type="button" className="wo-btn-add-row" onClick={addGarmentRow}>
                  + Add Row
                </button>
              </div>
              <div className="wo-table-wrapper">
                <table className="wo-edit-table wo-garment-table">
                  <thead>
                    <tr>
                      <th>Brand</th>
                      <th>SKU</th>
                      <th>Color</th>
                      <th>XS</th>
                      <th>S</th>
                      <th>M</th>
                      <th>L</th>
                      <th>XL</th>
                      <th>2XL</th>
                      <th>3XL</th>
                      <th>4XL</th>
                      <th>5XL</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.garment_details.map((garment, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={garment.brand}
                            onChange={(e) => handleGarmentChange(index, 'brand', e.target.value)}
                            placeholder="Brand"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={garment.sku}
                            onChange={(e) => handleGarmentChange(index, 'sku', e.target.value)}
                            placeholder="SKU"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={garment.color}
                            onChange={(e) => handleGarmentChange(index, 'color', e.target.value)}
                            placeholder="Color"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={garment.xs}
                            onChange={(e) => handleGarmentChange(index, 'xs', e.target.value)}
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={garment.s}
                            onChange={(e) => handleGarmentChange(index, 's', e.target.value)}
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={garment.m}
                            onChange={(e) => handleGarmentChange(index, 'm', e.target.value)}
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={garment.l}
                            onChange={(e) => handleGarmentChange(index, 'l', e.target.value)}
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={garment.xl}
                            onChange={(e) => handleGarmentChange(index, 'xl', e.target.value)}
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={garment.xxl}
                            onChange={(e) => handleGarmentChange(index, 'xxl', e.target.value)}
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={garment.xxxl}
                            onChange={(e) => handleGarmentChange(index, 'xxxl', e.target.value)}
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={garment.xxxxl}
                            onChange={(e) => handleGarmentChange(index, 'xxxxl', e.target.value)}
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={garment.xxxxxl}
                            onChange={(e) => handleGarmentChange(index, 'xxxxxl', e.target.value)}
                            min="0"
                          />
                        </td>
                        <td>
                          {formData.garment_details.length > 1 && (
                            <button
                              type="button"
                              className="wo-btn-remove-row"
                              onClick={() => removeGarmentRow(index)}
                            >
                              &times;
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="wo-form-section">
              <h4>Totals</h4>
              <div className="wo-form-row wo-totals-row">
                <div className="wo-form-group">
                  <label htmlFor="total_with_tax">Total w/Tax ($)</label>
                  <input
                    type="number"
                    id="total_with_tax"
                    name="total_with_tax"
                    value={formData.total_with_tax}
                    onChange={handleChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="wo-form-group">
                  <label htmlFor="deposit">Deposit ($)</label>
                  <input
                    type="number"
                    id="deposit"
                    name="deposit"
                    value={formData.deposit}
                    onChange={handleChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="wo-form-group">
                  <label htmlFor="total_due">Total Due ($)</label>
                  <input
                    type="text"
                    id="total_due"
                    name="total_due"
                    value={formData.total_due}
                    readOnly
                    placeholder="0.00"
                    className="wo-readonly-input wo-total-due"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="wo-form-section">
              <h4>Notes</h4>
              <div className="wo-form-row">
                <div className="wo-form-group wo-form-group-half">
                  <label htmlFor="notes">Customer Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Notes visible to customer..."
                    rows="3"
                  />
                </div>
                <div className="wo-form-group wo-form-group-half">
                  <label htmlFor="internal_notes">Internal Notes</label>
                  <textarea
                    id="internal_notes"
                    name="internal_notes"
                    value={formData.internal_notes}
                    onChange={handleChange}
                    placeholder="Internal notes (not on PDF)..."
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="wo-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="wo-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="wo-btn-submit"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingJob ? 'Update Work Order' : 'Create Work Order')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkOrderModal;
